/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * COMPONENTE: ModalNovaTarefa
 * VERSÃO:  1.0.0
 * ------------------------------------------------------------
 * Modal para o perito criar tarefa manual com categoria e
 * prioridade selecionáveis.
 * ============================================================
 */

import React, { useState, useEffect } from 'react';
import DiligenciaService, { CATEGORIA, PRIORIDADE } from '../../services/DiligenciaService';

const CATEGORIAS_MANUAIS = [
  { value: CATEGORIA.MANUAL,      label: 'Manual (ação do perito)' },
  { value: CATEGORIA.VERIFICACAO, label: 'Verificação técnica' },
  { value: CATEGORIA.DIVERGENCIA, label: 'Checar divergência' },
  { value: CATEGORIA.CONTATO,     label: 'Contato' },
  { value: CATEGORIA.LEMBRETE,    label: 'Lembrete' },
];

const PRIORIDADES_MANUAIS = [
  { value: PRIORIDADE.ALTISSIMA,      label: '🔴 Altíssima' },
  { value: PRIORIDADE.ALTA,           label: '🟠 Alta' },
  { value: PRIORIDADE.MEDIA,          label: '🟡 Média' },
  { value: PRIORIDADE.BAIXA,          label: '🔵 Baixa' },
  { value: PRIORIDADE.SEM_PRIORIDADE, label: '⚪ Sem prioridade' },
];

export default function ModalNovaTarefa({
  aberto,
  onFechar,
  onCriada,
  ordemId,
  laudoUuid = null,
  imovelId = null,
  imovelUuid = null,
}) {
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState(CATEGORIA.MANUAL);
  const [prioridade, setPrioridade] = useState(PRIORIDADE.MEDIA);
  const [prazo, setPrazo] = useState('');
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);

  /* Reset ao abrir */
  useEffect(() => {
    if (aberto) {
      const daquiUmaHora = new Date(Date.now() + 3600000);
      const localISO = new Date(daquiUmaHora.getTime() - daquiUmaHora.getTimezoneOffset() * 60000)
        .toISOString().slice(0, 16);
      setPrazo(localISO);
      setTitulo('');
      setDescricao('');
      setCategoria(CATEGORIA.MANUAL);
      setPrioridade(PRIORIDADE.MEDIA);
      setErro(null);
    }
  }, [aberto]);

  /* ESC fecha */
  useEffect(() => {
    if (!aberto) return;
    const h = e => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [aberto, onFechar]);

  const handleSubmeter = async () => {
    if (!titulo.trim()) {
      setErro('Título é obrigatório');
      return;
    }

    setLoading(true);
    setErro(null);

    try {
      const prazoISO = prazo ? new Date(prazo).toISOString() : new Date().toISOString();

      const nova = await DiligenciaService.criarTarefaManual({
        ordemId,
        laudoUuid,
        imovelId,
        imovelUuid,
        titulo: titulo.trim(),
        descricao: descricao.trim(),
        prazo: prazoISO,
        prioridade,
      });

      onCriada?.(nova);
      onFechar();
    } catch (e) {
      setErro(e.message || 'Falha ao criar tarefa');
    } finally {
      setLoading(false);
    }
  };

  if (!aberto) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
      <div className="bg-white w-full max-w-lg rounded-[2rem] shadow-2xl overflow-hidden">

        {/* HEADER */}
        <div className="bg-slate-900 p-6 flex items-center justify-between">
          <h3 className="text-white font-black uppercase text-sm tracking-wider">
            Nova Tarefa
          </h3>
          <button
            onClick={onFechar}
            className="text-slate-400 hover:text-white font-black text-xl"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">

          {erro && (
            <div className="bg-red-100 text-red-800 border-2 border-red-300 p-3 rounded-xl text-[11px] font-black uppercase">
              ⚠ {erro}
            </div>
          )}

          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
              Título *
            </label>
            <input
              type="text"
              value={titulo}
              onChange={e => setTitulo(e.target.value)}
              placeholder="Ex: Fotografar fachada com ângulo específico"
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:border-slate-900 outline-none"
              maxLength={100}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
              Descrição
            </label>
            <textarea
              value={descricao}
              onChange={e => setDescricao(e.target.value)}
              placeholder="Detalhes, contexto ou exigência que motiva a tarefa"
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:border-slate-900 outline-none min-h-[80px]"
              maxLength={500}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
                Categoria
              </label>
              <select
                value={categoria}
                onChange={e => setCategoria(e.target.value)}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:border-slate-900 outline-none"
              >
                {CATEGORIAS_MANUAIS.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
                Prioridade
              </label>
              <select
                value={prioridade}
                onChange={e => setPrioridade(e.target.value)}
                className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:border-slate-900 outline-none"
              >
                {PRIORIDADES_MANUAIS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
              Prazo
            </label>
            <input
              type="datetime-local"
              value={prazo}
              onChange={e => setPrazo(e.target.value)}
              className="w-full border-2 border-slate-200 rounded-xl px-4 py-3 text-sm font-medium focus:border-slate-900 outline-none"
            />
          </div>

        </div>

        {/* FOOTER */}
        <div className="p-6 bg-slate-50 border-t-2 border-slate-200 flex items-center justify-end gap-3">
          <button
            onClick={onFechar}
            disabled={loading}
            className="px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider text-slate-600 hover:bg-slate-200 transition-all disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmeter}
            disabled={loading || !titulo.trim()}
            className="bg-slate-900 text-white px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-wider hover:bg-slate-700 transition-all disabled:opacity-50"
          >
            {loading ? 'Criando...' : 'Criar Tarefa'}
          </button>
        </div>

      </div>
    </div>
  );
}